import React, { useEffect, useState } from 'react'
import { apiJson } from '../api/client'
import '../styles/components/fan-preferences.css'

const statusLabels = {
  NO_INBOUND: 'No incoming message', OUTBOUND_OBSERVED: 'Outgoing message recorded',
  QUEUED: 'Queued', GENERATING: 'Generating', VALIDATING: 'Validating', SENDING: 'Sending',
  PROCESSING: 'Processing', STOPPED: 'Stopped by a control', FAILED: 'Graph failed',
  PARTIAL_DELIVERY: 'Partially delivered — review required', DELIVERY_UNKNOWN: 'Delivery unknown — do not retry',
  UNASSIGNED: 'No matching graph in recent history', COMPLETED_WITHOUT_OBSERVED_REPLY: 'Graph completed; no reply recorded',
}
const hash = (value) => value ? `${value.slice(0, 12)}…` : 'Unknown'

function DeliveryReview({ data, onSaved }) {
  const [checked, setChecked] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  if (!data.can_reconcile || !data.delivery?.durable || !data.delivery.owner_token || !['PARTIAL', 'UNKNOWN', 'DISPATCHING'].includes(data.delivery.status)) return null
  const save = async () => {
    setBusy(true); setError('')
    try {
      await apiJson(`/bot/models/${data.model_id}/fans/${data.fan_id}/runtime/${data.graph_id}/acknowledge-review`, {
        method: 'POST', suppressErrorToast: true, body: JSON.stringify({ owner_token: data.delivery.owner_token, note }),
      })
      onSaved()
    } catch { setError('Could not acknowledge. Refresh and check whether the attempt changed.') }
    finally { setBusy(false) }
  }
  return <details className="fan-pref-evidence"><summary>Acknowledge an inspected delivery incident</summary>
    <p>This closes the incident hold for future graphs only. It does not resend this graph, cancel an already accepted provider request, or remove manual mode/model pause. Inspect the provider conversation first.</p>
    <label>Reconciliation note<textarea rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} /></label>
    <label className="fan-runtime-confirm"><input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} disabled={busy} />I inspected delivery and handled any missing parts</label>
    <button type="button" className="v2-btn v2-btn-ghost" disabled={busy || !checked || note.trim().length < 3} onClick={save}>{busy ? 'Saving…' : 'Close incident without resending'}</button>
    {error && <p role="alert">{error}</p>}
  </details>
}

export default function FanRuntimePanel({ modelId, fanId }) {
  const key = `${modelId}:${fanId}`
  const [open, setOpen] = useState(false)
  const [refresh, setRefresh] = useState(0)
  const [state, setState] = useState({ key: '', data: null, error: '' })
  useEffect(() => {
    if (!open) return undefined
    const controller = new AbortController()
    let alive = true
    setState({ key, data: null, error: '' })
    apiJson(`/bot/models/${modelId}/fans/${fanId}/runtime`, { signal: controller.signal, suppressErrorToast: true })
      .then((data) => {
        if (!alive) return
        if (Number(data.model_id) !== Number(modelId) || Number(data.fan_id) !== Number(fanId)) throw new Error('Scope mismatch')
        setState({ key, data, error: '' })
      }).catch(() => { if (alive) setState({ key, data: null, error: 'Runtime diagnostics unavailable. Refresh or check access.' }) })
    return () => { alive = false; controller.abort() }
  }, [key, modelId, fanId, open, refresh])
  const data = state.key === key ? state.data : null
  const error = state.key === key ? state.error : ''
  return <details className="v2-inspector-section fan-preferences-panel" onToggle={(e) => setOpen(e.currentTarget.open)}>
    <summary>Reply status &amp; configuration</summary>
    {open && <div className="fan-pref-body">
      {error ? <p role="alert">{error}</p> : !data ? <p role="status">Loading reply status…</p> : <>
        <strong>{statusLabels[data.status] || 'Unknown state'}</strong>
        <p className="fan-pref-muted">Read only. Opening this panel never sends or retries a message.</p>
        <dl className="fan-runtime-facts">
          <div><dt>Waiting</dt><dd>{data.wait_seconds == null ? 'Not recorded' : `${Math.round(data.wait_seconds / 60)} min`}</dd></div>
          <div><dt>Node / graph</dt><dd>{data.node || '—'} / {data.graph_status || '—'}</dd></div>
          <div><dt>Delivery</dt><dd>{data.delivery?.status || data.provider_confirmation || 'Unknown'}</dd></div>
          <div><dt>Confirmed parts</dt><dd>{data.delivery?.confirmed_parts?.length ?? 'Unknown'}</dd></div>
          <div><dt>Used configuration</dt><dd>#{data.used?.config_id ?? '—'} · {hash(data.used?.sha256)}</dd></div>
          <div><dt>Current configuration</dt><dd>#{data.configured?.config_id ?? '—'} · {hash(data.configured?.sha256)}</dd></div>
        </dl>
        {(data.current_config_matches_used === false || data.config_changed_during_graph) && <p className="fan-pref-warning">Configuration differs. The recorded response may have used an older or mixed version.</p>}
        {data.current_config_matches_used == null && <p className="fan-pref-muted">No comparable historical configuration fingerprint.</p>}
        {!!data.reason_codes?.length && <p className="fan-pref-warning">{data.reason_codes.join(' · ').replaceAll('_', ' ')}</p>}
        <details className="fan-pref-evidence"><summary>Decision and recorded work</summary>
          <p>Commercial objective: {data.decision?.commercial_objective || 'Unknown'}</p>
          <p>Authorized by existing Core: {Object.entries(data.decision?.permissions || {}).filter(([, allowed]) => allowed).map(([name]) => name).join(', ') || 'Not recorded'}</p>
          {!!data.decision?.conflicts?.length && <p className="fan-pref-warning">{data.decision.conflicts.join(' · ')}</p>}
          {Object.entries(data.observed_node_duration_ms || {}).map(([name, ms]) => <p key={name}>{name}: {(ms / 1000).toFixed(1)} s</p>)}
          <p className="fan-pref-muted">Recorded node work is not end-to-end queue or provider latency. A message ID does not prove that the entire conversational request was answered.</p>
        </details>
        <DeliveryReview key={`${key}:${data.graph_id}:${data.delivery?.owner_token}`} data={data} onSaved={() => setRefresh((v) => v + 1)} />
      </>}
      <button type="button" className="v2-btn v2-btn-ghost" onClick={() => setRefresh((v) => v + 1)}>Refresh status</button>
    </div>}
  </details>
}
